/**
 * Site settings - single source of truth for dynamic app settings.
 *
 * Values live in Firestore `settings/site` (written by AdminSettings). This is
 * the authoritative source on BOTH web and mobile. Remote Config remains only
 * as a bootstrap/fallback layer for `ad_expiry_days` (see adExpiry.js).
 */

import { settingsRef } from './collections';

const SITE_SETTINGS_DEFAULTS = {
  maintenance_mode: false,
  max_images_per_ad: 12,
  ad_expiry_days: 30,
  enable_video_upload: false,
  min_app_version: '1.0.0',
  require_paid_plan_for_phone: false,
};

let cached = null;

/**
 * Fetch and cache the `settings/site` doc. Call once at app boot.
 * Returns a promise that always resolves with the settings object.
 */
export async function refreshSiteSettings() {
  try {
    const snap = await settingsRef().doc('site').get();
    cached = snap.exists ? { ...SITE_SETTINGS_DEFAULTS, ...snap.data() } : null;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('refreshSiteSettings failed:', err.message);
    }
    cached = null;
  }
  return cached || { ...SITE_SETTINGS_DEFAULTS };
}

/**
 * Get the full cached settings object (defaults until refreshed).
 */
export function getSiteSettings() {
  return cached || { ...SITE_SETTINGS_DEFAULTS };
}

/**
 * Get a single setting.
 * @param {string} key
 * @param {*} [fallback] - used when the key is not present in Firestore/doc
 */
export function getSiteSetting(key, fallback) {
  const settings = getSiteSettings();
  const value = settings[key];
  if (value !== undefined && value !== null) return value;
  return fallback !== undefined ? fallback : SITE_SETTINGS_DEFAULTS[key];
}

export { SITE_SETTINGS_DEFAULTS };
